package com.evoapps.evoshift.domain.model

/*
 * SH-95: labour % is measured against the operator's INCOME, not the venue's whole turnover. A
 * commission agent or franchisee keeps only the commission on the sales, so labour is a share of
 * that:
 *
 *   income  = net sales × commission % + fixed revenue (the day's share, as in its result)
 *   labour % = employer labour cost ÷ income × 100
 *
 * A workplace without a commission (0, the default) keeps the whole net sales: its income is the
 * net sales plus the fixed revenue. The fixed monthly revenue always counts, spread evenly over all
 * the days of the month ([fixedRevenueShare]): the days' shares add up to the month's amount, so a
 * day's labour % and the month's are measured against the same income. The income is the same as the day's result
 * (SH-46) without the ledger's revenue items (a sublease or a catering order is booked on one
 * day; it does not carry that day's staff). A month's income is the sum of its days'.
 */

/** What a labour % is measured against. */
enum class LabourBase {
    /** The operator's income: the commission on the net sales plus the fixed revenue. */
    INCOME,
    /** Net sales: a workplace without a commission keeps the whole sales. */
    SALES
}

object LabourShare {
    /**
     * [LabourBase.INCOME] with a commission (a fraction above 0) or a fixed revenue, else
     * [LabourBase.SALES] (the net sales are all the income).
     */
    fun baseOf(commissionPercent: Double, fixedRevenue: Double = 0.0): LabourBase =
        if (commissionPercent > 0 || fixedRevenue > 0) LabourBase.INCOME else LabourBase.SALES

    /**
     * What the owner keeps of [netSales]: the commission at [commissionPercent] (a fraction,
     * 0.252 = 25.2%), or without a commission the whole net sales (an ordinary business keeps its
     * own sales). The part of the owner's income that comes from the sales (the result's revenue
     * adds the fixed revenue and the ledger's revenue items).
     */
    fun salesIncome(netSales: Double, commissionPercent: Double): Double =
        if (commissionPercent > 0) netSales * commissionPercent else netSales

    /** What labour is measured against: the [salesIncome] plus the day's (or month's) [fixedRevenue]. */
    fun baseAmount(netSales: Double, commissionPercent: Double, fixedRevenue: Double = 0.0): Double =
        salesIncome(netSales, commissionPercent) + fixedRevenue

    /**
     * [date]'s share of a fixed monthly revenue of [monthly]: an equal share of every day of the
     * month, in whole cents (rounded down), and the last day takes the remainder, so the shares
     * add up to [monthly] exactly. functions/fixedRevenue.js splits it the same way.
     */
    fun fixedRevenueShare(monthly: Double, date: kotlinx.datetime.LocalDate): Double {
        if (monthly <= 0.0 || monthly.isNaN()) return 0.0
        val days = daysInMonth(date)
        val totalCents = kotlin.math.round(monthly * 100).toLong()
        val dayCents = totalCents / days
        val cents = if (date.day == days) totalCents - dayCents * (days - 1) else dayCents
        return cents / 100.0
    }

    private fun daysInMonth(date: kotlinx.datetime.LocalDate): Int {
        val first = kotlinx.datetime.LocalDate(date.year, date.month, 1)
        val next = if (date.month == kotlinx.datetime.Month.DECEMBER) kotlinx.datetime.LocalDate(date.year + 1, 1, 1)
            else kotlinx.datetime.LocalDate(date.year, date.month.ordinal + 2, 1)
        return (next.toEpochDays() - first.toEpochDays()).toInt()
    }

    /** [labourCost] as a share of [base], in percent; null without a base to share. */
    fun percent(labourCost: Double, base: Double): Double? = if (base > 0) labourCost / base * 100 else null

    /**
     * A labour % of income as the equivalent share of net sales at [commissionPercent], leaving
     * the fixed revenue out (orientation for the target: 60% of income at 25.2% is ≈ 15.1% of sales).
     */
    fun salesEquivalent(incomePercent: Double, commissionPercent: Double): Double =
        if (commissionPercent > 0) incomePercent * commissionPercent else incomePercent

    /** A labour % of net sales as a share of the income at [commissionPercent] (the inverse of [salesEquivalent]). */
    fun incomeEquivalent(salesPercent: Double, commissionPercent: Double): Double =
        if (commissionPercent > 0) salesPercent / commissionPercent else salesPercent
}
